import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { TServerSideProps } from '@Hotels-at-Home/hah-fe-core/dist/types';

import { wrapper } from '~/store';
import { usePageViewEvent } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';
import { getResolvedPost, getRoutePostLink } from '~/utils/blog';
import LandingPage from '~/ui/pages/Blog/LandingPage';
import PostPage from '~/ui/pages/Blog/PostPage';

import { catalogId, region } from '~/constants/siteConfig';
import { LP_ARTICLE_NAME, COMMON_ARTICLE_NAME } from '~/constants/blog';

interface IPost {
  id: number;
  isLP: boolean;
}

type TProps = TServerSideProps<IPost>;

const Post = (props: TProps) => {
  const { id, isLP, error } = props;

  const articleName = isLP ? LP_ARTICLE_NAME : COMMON_ARTICLE_NAME.replace('[id]', String(id));

  const { t: tArticle } = useTranslation('', { keyPrefix: `$local.$articles.${articleName}` });

  usePageViewEvent();

  const meta = {
    title: tArticle('pageTitle'),
    description: tArticle('metaDescription'),
  };

  return (
    <NextPage error={error} meta={meta}>
      {isLP ? <LandingPage /> : <PostPage id={id} />}
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
  () => async ctx => {
    const { query, locale } = ctx;

    const props: TProps = {
      ...(await serverSideTranslations(locale)),
      id: null,
      isLP: false,
      error: null,
    };
    let notFound = false;

    const path = query.path as string[];
    const url = path.join('/');

    // Redirect based on URL key
    // Checks if we need to redirect, if the URL key in the browser is different from the one in the response
    const resolvedPost = getResolvedPost(url, locale);
    const resolvedPostUrl = resolvedPost?.url;

    if (!resolvedPostUrl) {
      props.error = `Post was not found: ${path}`;
      notFound = true;
      return { props, notFound };
    }

    // If current "postUrl" and response "postUrl" are differ -
    // make a redirect to a new "postUrl"
    if (resolvedPostUrl !== url) {
      let destination = getRoutePostLink(resolvedPostUrl, locale);
      if ('referrer' in query) {
        destination += `?referrer=${query.referrer}`;
      }
      if ('___store' in query) {
        destination += `?___store=${catalogId}${region}`;
      }
      return {
        redirect: {
          destination,
          permanent: false,
        },
      };
    }

    props.id = resolvedPost?.id;
    props.isLP = resolvedPost?.isLP;

    return { props, notFound };
  },
);

export default Post;
